import { useApp } from '@/contexts';

import { useMemo, useState } from 'react';

import { useGetEnvironmentsQuery } from '@features/environments';
import { Download as DownloadIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { Link, useSearchParams } from 'react-router';

import { useAppDispatch } from '@/app/hooks';
import { PageLoader } from '@/components/common';
import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import { PageContainer, PageHeading } from '@/components/layout';
import type { ExecutionStep } from '@/types';

import {
  transformersApi,
  useGetExecutionLogsByGuidQuery,
  useGetExecutionMetadataByGuidQuery,
  useGetTransformersQuery,
} from '../api/transformersApi';
import { LogResultTable } from '../components/LogResultTable';

/**
 * Execution Details page - shows detailed logs and step-by-step execution breakdown
 * Matches dataProtect's monitorTransPopUp.js styling and functionality
 *
 * URL: /a/:appId/health?executionGuid=xxx&env=xxx
 */
export const ExecutionDetailsPage = () => {
  const { appId, tenantId } = useApp();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const executionGuid = searchParams.get('executionGuid');
  const environmentId = searchParams.get('env');

  // Fetch transformers/recipes to get recipe name
  const { data: transformers } = useGetTransformersQuery(appId!, { skip: !appId });

  // Fetch environments to get environment name and color
  const { data: environments } = useGetEnvironmentsQuery(
    {
      tenantId,
      appId: appId!,
    },
    { skip: !tenantId || !appId }
  );

  // Fetch execution metadata
  const {
    data: executionMetadata,
    isLoading: isLoadingMetadata,
    error: metadataError,
  } = useGetExecutionMetadataByGuidQuery(
    {
      tenantId: String(tenantId),
      executionGuid: executionGuid!,
    },
    { skip: !tenantId || !executionGuid }
  );

  // Fetch execution logs/steps
  const {
    data: logs,
    isLoading: isLoadingLogs,
    error: logsError,
  } = useGetExecutionLogsByGuidQuery(
    {
      tenantId: String(tenantId),
      executionGuid: executionGuid!,
    },
    { skip: !tenantId || !executionGuid }
  );

  const execution = executionMetadata?.[0];
  const isLoading = isLoadingMetadata || isLoadingLogs;
  const error = metadataError || logsError;

  // Get environment details from environments list
  const environment = environments?.find((e) => String(e.id) === String(environmentId));

  // Get recipe/transformer details
  const baseId = execution?.executionMetadata?.baseId || execution?.baseId;
  const recipe = transformers?.find((t) => String(t.baseId) === String(baseId));
  const recipeName = recipe?.name || execution?.executionMetadata?.recipeName || execution?.name;

  // Calculate status
  const getStatus = () => {
    if (!execution) return null;

    if (execution.statusText?.toLowerCase().includes('partial')) {
      return { label: 'Partial Success', color: 'warning' as const, bgColor: 'yellow' };
    }
    if (execution.succeeded) {
      return { label: 'Success', color: 'success' as const, bgColor: 'green' };
    }
    if (!execution.completed && !execution.succeeded) {
      return { label: 'In Progress', color: 'info' as const, bgColor: '#f0ad4e' };
    }
    return { label: 'Failed', color: 'error' as const, bgColor: '#d9534f' };
  };

  const status = getStatus();

  // Format duration timer
  const formatTimer = (seconds?: number) => {
    if (!seconds) return '0s';
    const time = new Date(seconds * 1000).toISOString().substr(11, 11);
    let formatted = time;
    if (formatted.substr(0, 2) === '00') {
      formatted = formatted.substr(3);
    }
    if (formatted.substr(0, 1) === '0') {
      formatted = formatted.substr(1);
    }
    return formatted;
  };

  const [isDownloading, setIsDownloading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleDownloadLogs = async () => {
    if (!execution || isDownloading) return;
    setIsDownloading(true);

    try {
      const finishTime = execution.finishTime || execution.startTime;
      if (!finishTime) {
        showSnackbar('No finish time available for this execution.', 'warning');
        return;
      }

      const splitStr = finishTime.split('T');
      const splitDate = splitStr[0].split('-');
      const formattedDate = splitDate[2] + splitDate[1] + splitDate[0]; // DDMMYYYY

      // Use transId (not baseId) to match dataProtect's downloadLogsFromS3
      const recipeId = execution.transId || execution.baseId;
      if (!recipeId) {
        showSnackbar('Recipe ID not found for this execution.', 'error');
        return;
      }

      const fileName = `${execution.executionGUID}.zip`; // Use executionGUID (UUID format), not executionGuid from URL

      // Get presigned S3 URL from API
      const downloadUrl = await dispatch(
        transformersApi.endpoints.getExecutionLogsDownloadUrl.initiate(
          {
            tenantId,
            appId: appId!,
            envId: environmentId!,
            date: formattedDate,
            recipeId,
            fileName,
          },
          { forceRefetch: true }
        )
      ).unwrap();

      // Direct download using DOM element (no fetch, avoids CORS)
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      showSnackbar('Execution logs downloaded successfully.', 'success');
    } catch (err) {
      console.error('Error downloading execution logs:', err);
      showSnackbar('Execution logs are not available for this job.', 'warning');
    } finally {
      setIsDownloading(false);
    }
  };

  // Apply sequential step filtering to match dataProtect behavior:
  // Show completed steps, but stop after a failure (unless onError='continue')
  // This ensures steps after an error are not displayed
  const filteredLogs = useMemo((): ExecutionStep[] => {
    if (!logs || logs.length === 0) return [];
    const result: ExecutionStep[] = [];
    for (const step of logs as ExecutionStep[]) {
      if (step.completed) {
        result.push(step);
        if (!step.succeeded && step.onError !== 'continue') {
          break;
        }
      }
    }
    return result;
  }, [logs]);

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error">
          Failed to load execution details.
          <br />
          <Typography variant="caption" component="pre" sx={{ mt: 1, fontSize: 11 }}>
            {JSON.stringify(error, null, 2)}
          </Typography>
        </Alert>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeading title="Execution Details" subtitle="Loading execution logs and metadata..." />
        <PageLoader variant="section" />
      </PageContainer>
    );
  }

  if (!execution) {
    return (
      <PageContainer>
        <Alert severity="warning">
          No execution data found for GUID: {executionGuid}
          <br />
          <Typography variant="caption" component="pre" sx={{ mt: 1, fontSize: 11 }}>
            {JSON.stringify({ executionMetadata, logs }, null, 2)}
          </Typography>
        </Alert>
      </PageContainer>
    );
  }

  const isWizard = execution?.executionMetadata?.generatedFromPi;
  const recipeLink = isWizard ? `/a/${appId}/wiz/${baseId}` : `/a/${appId}/tf/${baseId}`;

  return (
    <PageContainer>
      <PageHeading
        title="Execution Details"
        subtitle={`Recipe: ${recipeName || 'Unknown'} • GUID: ${executionGuid}`}
      />
      {/* Header Section - matches dataProtect styling */}
      <Paper
        sx={{
          mb: 0.125,
          bgcolor: '#efefef',
          p: '6px 8px 8px 5px',
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Status Indicator */}
          {status && (
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: status.bgColor,
                border: '1px solid rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 'bold',
                color: status.bgColor === 'yellow' ? '#000' : '#fff',
              }}
            >
              {status.label === 'Failed' && '!'}
            </Box>
          )}

          {/* Compact execution info - matches old dataProtect format */}
          <Typography variant="body2" component="span">
            {execution?.startTime && format(new Date(execution.startTime), 'MMM do, h:mm:ss a')}
          </Typography>

          <Typography variant="body2" component="span" sx={{ ml: 2 }}>
            <strong>Recipe:</strong>{' '}
            <Link
              to={recipeLink}
              style={{ color: '#0066cc', textDecoration: 'none' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {recipeName}
            </Link>
            {baseId && <span style={{ color: '#666' }}> ({baseId})</span>}
          </Typography>

          <Typography variant="body2" component="span" sx={{ ml: 2 }}>
            <strong>Total Timer Id:</strong> {formatTimer(execution?.timer)}
          </Typography>

          <Typography variant="body2" component="span" sx={{ ml: 2 }}>
            <strong>Recipe Version:</strong> {execution?.recipeVersionNumber || 1}
          </Typography>

          <Typography variant="body2" component="span" sx={{ ml: 2 }}>
            <strong>Type:</strong> {execution?.triggerType || 'Manual'}
          </Typography>

          {environment && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <Typography variant="body2" component="span">
                <strong>Environment:</strong>
              </Typography>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: environment.color || '#404040',
                  border: '1px solid black',
                  borderRadius: 0.5,
                  mx: 0.5,
                }}
              />
              <Typography variant="body2" component="span">
                {environment.name}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" component="span" sx={{ ml: 2 }}>
            <strong>Id:</strong>{' '}
            {(execution?.externalExecutionId || executionGuid)?.toString().replace(/\\+$/, '')}
          </Typography>
        </Box>

        {/* Download Logs Button */}
        <Tooltip title={isDownloading ? 'Downloading...' : 'Download Execution Logs'}>
          <span>
            <IconButton
              size="small"
              onClick={handleDownloadLogs}
              disabled={isDownloading}
              sx={{ opacity: isDownloading ? 1 : 0.6 }}
            >
              {isDownloading ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Paper>

      {/* Table Header - matches dataProtect styling */}
      <Paper
        sx={{
          mb: 0.125,
          bgcolor: '#CCCDCF',
          p: '6px 8px 8px 5px',
          fontSize: 14,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ width: '30%', ml: '1%' }}>
            <strong>Statement:</strong>
          </Box>
          <Box sx={{ width: '42%', ml: '1%' }}>
            <strong>Result:</strong>
          </Box>
          <Box sx={{ width: '11%', ml: '1%' }}>
            <strong>Rows Read:</strong>
          </Box>
          <Box sx={{ width: '13%', ml: '1%' }}>
            <strong>Timer:</strong>
          </Box>
        </Box>
      </Paper>

      {/* Logs Table */}
      <Box
        sx={{
          maxHeight: '88vh',
          overflowY: 'auto',
          mb: 2.5,
          mt: 0.5,
        }}
      >
        {filteredLogs && filteredLogs.length > 0 ? (
          filteredLogs.map((log: ExecutionStep, index: number) => {
            const isSchema = log.statement?.includes('printschema');
            const isTable = !isSchema && log.statement?.toLowerCase().includes('print');
            const hasExtendedProps = log.extendedProperties && log.extendedProperties !== '';

            // Determine if this is an error row
            const isError = log.succeeded === false;

            return (
              <Box
                key={log.statement ? `${log.statement}-${index}` : index}
                sx={{
                  display: 'flex',
                  gap: 1,
                  py: 1,
                  px: 0,
                  mb: 0.125,
                  bgcolor: 'white',
                  borderLeft: isError ? '3px solid #d9534f' : 'none',
                  borderBottom: '1px solid #ddd',
                  fontSize: '13px',
                }}
              >
                {/* Statement */}
                <Box sx={{ width: '30%', ml: '1%', wordBreak: 'break-word' }}>
                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                    {log.statement}
                  </Typography>
                </Box>

                {/* Result */}
                <Box sx={{ width: '42%', ml: '1%', wordBreak: 'break-word' }}>
                  {(() => {
                    if (isTable) return <LogResultTable result={log.result} />;
                    if (isSchema) {
                      return (
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            m: 0,
                          }}
                        >
                          {log.result}
                        </Typography>
                      );
                    }
                    if (hasExtendedProps) {
                      return (
                        <Typography variant="body2" sx={{ fontSize: '13px' }}>
                          {log.result}{' '}
                          <Link
                            to={`/a/${appId}/health/sfjobs?step=${log.stepGuid}&env=${environmentId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#0066cc', textDecoration: 'none' }}
                          >
                            (details)
                          </Link>
                        </Typography>
                      );
                    }
                    return (
                      <Typography variant="body2" sx={{ fontSize: '13px' }}>
                        {log.result}
                      </Typography>
                    );
                  })()}
                </Box>

                {/* Rows Read */}
                <Box sx={{ width: '11%', ml: '1%' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                    {log.recordsRead > 0 ? log.recordsRead.toLocaleString() : ''}
                  </Typography>
                </Box>

                {/* Timer */}
                <Box sx={{ width: '13%', ml: '1%' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                    {log.timer?.toFixed(5) || '0.00000'}
                  </Typography>
                </Box>
              </Box>
            );
          })
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No log data found.
            </Typography>
          </Paper>
        )}
      </Box>
    </PageContainer>
  );
};
